import React, { useState, useEffect } from "react";
import "./Dashboard.css";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(1);

  /* -------------------------- Projects -------------------------- */
  const [projects, setProjects] = useState([]);
  const [projectTitle, setProjectTitle] = useState("");
  const [projectDesc, setProjectDesc] = useState("");
  const [projectDetails, setProjectDetails] = useState(""); // ✅ Added details state
  const [projectImage, setProjectImage] = useState("");
  const [projectLink, setProjectLink] = useState("");
  const [projectCategory, setProjectCategory] = useState("");

  /* -------------------------- Qualifications -------------------------- */
  const [qualifications, setQualifications] = useState([]);
  const [qualTitle, setQualTitle] = useState("");
  const [qualSubtitle, setQualSubtitle] = useState("");
  const [qualDuration, setQualDuration] = useState("");
  const [qualContributions, setQualContributions] = useState("");

  /* -------------------------- Services -------------------------- */
  const [services, setServices] = useState([]);
  const [serviceTitle, setServiceTitle] = useState("");
  const [serviceDesc, setServiceDesc] = useState("");
  const [serviceFeatures, setServiceFeatures] = useState("");

  /* -------------------------- Blogs -------------------------- */
  const [blogs, setBlogs] = useState([]);
  const [blogTitle, setBlogTitle] = useState("");
  const [blogSummary, setBlogSummary] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [blogImage, setBlogImage] = useState("");
  const [blogCategory, setBlogCategory] = useState("");
  const [blogDate, setBlogDate] = useState("");

  const API_BASE = "http://localhost:5000/api";

  /* -------------------------- Fetch Data -------------------------- */
  useEffect(() => {
    fetchProjects();
    fetchQualifications();
    fetchServices();
    fetchBlogs();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/projects`);
      const data = await res.json();
      setProjects(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchQualifications = async () => {
    try {
      const res = await fetch(`${API_BASE}/qualifications`);
      const data = await res.json();
      setQualifications(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_BASE}/services`);
      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBlogs = async () => {
    try {
      const res = await fetch(`${API_BASE}/blogs`);
      const data = await res.json();
      setBlogs(data);
    } catch (err) {
      console.error(err);
    }
  };

  /* -------------------------- Projects CRUD -------------------------- */
  const addProject = async () => {
    if (!projectTitle || !projectDesc) {
      alert("Please fill all required fields");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: projectTitle,
          description: projectDesc,
          details: projectDetails, // ✅ Added details
          image: projectImage,
          link: projectLink,
          category: projectCategory || "Other",
        }),
      });
      const data = await res.json();
      if (data.success) {
        const newProject = {
          id: data.id,
          title: projectTitle,
          description: projectDesc,
          details: projectDetails, // ✅ Added details
          image: projectImage,
          link: projectLink,
          category: projectCategory || "Other",
        };
        setProjects([...projects, newProject]);
        setProjectTitle("");
        setProjectDesc("");
        setProjectDetails(""); // ✅ Reset details
        setProjectImage("");
        setProjectLink("");
        setProjectCategory("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateProject = async (id) => {
    const title = prompt("New Project Title:");
    const description = prompt("New Project Description:");
    const details = prompt("New Project Details:"); // ✅ Prompt for details
    const image = prompt("New Project Image URL:");
    const link = prompt("New Project Link:");
    const category = prompt("New Category:");
    if (!title || !description) return;
    try {
      const res = await fetch(`${API_BASE}/projects/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, description, details, image, link, category }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedProjects = projects.map((p) => {
          if (p.id === id) {
            return { ...p, title, description, details, image, link, category }; // ✅ Include details
          } else {
            return p;
          }
        });
        setProjects(updatedProjects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteProject = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/projects/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        const filteredProjects = projects.filter((p) => p.id !== id);
        setProjects(filteredProjects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* -------------------------- Qualifications CRUD -------------------------- */
  const addQualification = async () => {
    if (!qualTitle || !qualSubtitle || !qualDuration) return;
    const contributions = qualContributions ? qualContributions.split(",") : [];
    try {
      const res = await fetch(`${API_BASE}/qualifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: qualTitle,
          subtitle: qualSubtitle,
          duration: qualDuration,
          contributions,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const newQual = {
          id: data.id,
          title: qualTitle,
          subtitle: qualSubtitle,
          duration: qualDuration,
          contributions,
        };
        setQualifications([...qualifications, newQual]);
        setQualTitle("");
        setQualSubtitle("");
        setQualDuration("");
        setQualContributions("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateQualification = async (id) => {
    const title = prompt("New Title:");
    const subtitle = prompt("New Subtitle:");
    const duration = prompt("New Duration:");
    const contributions = prompt("New Contributions (comma separated):").split(",");
    try {
      const res = await fetch(`${API_BASE}/qualifications/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, subtitle, duration, contributions }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedQuals = qualifications.map((q) => {
          if (q.id === id) {
            return { ...q, title, subtitle, duration, contributions };
          } else {
            return q;
          }
        });
        setQualifications(updatedQuals);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteQualification = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/qualifications/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        const filteredQuals = qualifications.filter((q) => q.id !== id);
        setQualifications(filteredQuals);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* -------------------------- Services CRUD -------------------------- */
  const addService = async () => {
    if (!serviceTitle || !serviceDesc) return;
    const features = serviceFeatures ? serviceFeatures.split(",") : [];
    try {
      const res = await fetch(`${API_BASE}/services`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: serviceTitle, desc: serviceDesc, features }),
      });
      const data = await res.json();
      if (data.success) {
        const newService = { id: data.id, title: serviceTitle, desc: serviceDesc, features };
        setServices([...services, newService]);
        setServiceTitle("");
        setServiceDesc("");
        setServiceFeatures("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateService = async (id) => {
    const title = prompt("New Service Title:");
    const desc = prompt("New Service Description:");
    const features = prompt("New Features (comma separated):").split(",");
    try {
      const res = await fetch(`${API_BASE}/services/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, desc, features }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedServices = services.map((s) => {
          if (s.id === id) {
            return { ...s, title, desc, features };
          } else {
            return s;
          }
        });
        setServices(updatedServices);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteService = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/services/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        const filteredServices = services.filter((s) => s.id !== id);
        setServices(filteredServices);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* -------------------------- Blogs CRUD -------------------------- */
  const addBlog = async () => {
    if (!blogTitle || !blogSummary || !blogContent) {
      alert("Please fill required fields");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/blogs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: blogTitle,
          summary: blogSummary,
          content: blogContent,
          image: blogImage,
          category: blogCategory || "General",
          date: blogDate || new Date().toISOString().split("T")[0],
        }),
      });
      const data = await res.json();
      if (data.success) {
        const newBlog = {
          id: data.id,
          title: blogTitle,
          summary: blogSummary,
          content: blogContent,
          image: blogImage,
          category: blogCategory || "General",
          date: blogDate || new Date().toISOString().split("T")[0],
        };
        setBlogs([...blogs, newBlog]);
        setBlogTitle("");
        setBlogSummary("");
        setBlogContent("");
        setBlogImage("");
        setBlogCategory("");
        setBlogDate("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateBlog = async (id) => {
    const title = prompt("New Blog Title:");
    const summary = prompt("New Summary:");
    const content = prompt("New Content:");
    const image = prompt("New Image URL:");
    const category = prompt("New Category:");
    const date = prompt("New Date (YYYY-MM-DD):");
    if (!title || !summary || !content) return;
    try {
      const res = await fetch(`${API_BASE}/blogs/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, summary, content, image, category, date }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedBlogs = blogs.map((b) => {
          if (b.id === id) {
            return { ...b, title, summary, content, image, category, date };
          } else {
            return b;
          }
        });
        setBlogs(updatedBlogs);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteBlog = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/blogs/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        const filteredBlogs = blogs.filter((b) => b.id !== id);
        setBlogs(filteredBlogs);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* -------------------------- Render -------------------------- */
  return (
    <div className="dashboard">
      <h1>Admin Dashboard</h1>

      <div className="dashboard-tabs">
        <button onClick={() => setActiveTab(1)}>Projects</button>
        <button onClick={() => setActiveTab(2)}>Qualifications</button>
        <button onClick={() => setActiveTab(3)}>Services</button>
        <button onClick={() => setActiveTab(4)}>Blogs</button>
      </div>

      {/* ---------------- Projects Section ---------------- */}
      {activeTab === 1 && (
        <div className="dashboard-section">
          <h2>Projects</h2>
          <div className="dashboard-form">
            <input
              placeholder="Title"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
            />
            <textarea
              placeholder="Description"
              value={projectDesc}
              onChange={(e) => setProjectDesc(e.target.value)}
            />
            <textarea
              placeholder="Details"                    // ✅ Added details field
              value={projectDetails}
              onChange={(e) => setProjectDetails(e.target.value)}
            />
            <input
              placeholder="Image URL"
              value={projectImage}
              onChange={(e) => setProjectImage(e.target.value)}
            />
            <input
              placeholder="Project Link"
              value={projectLink}
              onChange={(e) => setProjectLink(e.target.value)}
            />
            <input
              placeholder="Category (e.g. AI, Web, App)"
              value={projectCategory}
              onChange={(e) => setProjectCategory(e.target.value)}
            />
            <button onClick={addProject}>Add Project</button>
          </div>

          <ul>
            {projects.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong> – {p.description}
                {p.details && (                                // ✅ Display details
                  <p>
                    <em>Details:</em> {p.details}
                  </p>
                )}
                <p>
                  <em>Category:</em> <b>{p.category || "Other"}</b>
                </p>
                {p.image && (
                  <div>
                    <img
                      src={p.image}
                      alt={p.title}
                      width="100"
                      style={{ marginTop: "5px", borderRadius: "6px" }}
                    />
                  </div>
                )}
                {p.link && (
                  <p>
                    <a href={p.link} target="_blank" rel="noreferrer">
                      🔗 View Project
                    </a>
                  </p>
                )}
                <div className="dashboard-item-actions">
                  <button onClick={() => updateProject(p.id)}>Edit</button>
                  <button onClick={() => deleteProject(p.id)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------- Qualifications Section ---------------- */}
      {activeTab === 2 && (
        <div className="dashboard-section">
          <h2>Qualifications</h2>
          <div className="dashboard-form">
            <input
              placeholder="Title"
              value={qualTitle}
              onChange={(e) => setQualTitle(e.target.value)}
            />
            <input
              placeholder="Subtitle"
              value={qualSubtitle}
              onChange={(e) => setQualSubtitle(e.target.value)}
            />
            <input
              placeholder="Duration"
              value={qualDuration}
              onChange={(e) => setQualDuration(e.target.value)}
            />
            <input
              placeholder="Contributions (comma separated)"
              value={qualContributions}
              onChange={(e) => setQualContributions(e.target.value)}
            />
            <button onClick={addQualification}>Add Qualification</button>
          </div>

          <ul>
            {qualifications.map((q) => (
              <li key={q.id}>
                <strong>{q.title}</strong> – {q.subtitle} ({q.duration})
                <ul>
                  {q.contributions.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
                <div className="dashboard-item-actions">
                  <button onClick={() => updateQualification(q.id)}>Edit</button>
                  <button onClick={() => deleteQualification(q.id)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------- Services Section ---------------- */}
      {activeTab === 3 && (
        <div className="dashboard-section">
          <h2>Services</h2>
          <div className="dashboard-form">
            <input
              placeholder="Title"
              value={serviceTitle}
              onChange={(e) => setServiceTitle(e.target.value)}
            />
            <textarea
              placeholder="Description"
              value={serviceDesc}
              onChange={(e) => setServiceDesc(e.target.value)}
            />
            <input
              placeholder="Features (comma separated)"
              value={serviceFeatures}
              onChange={(e) => setServiceFeatures(e.target.value)}
            />
            <button onClick={addService}>Add Service</button>
          </div>

          <ul>
            {services.map((s) => (
              <li key={s.id}>
                <strong>{s.title}</strong> – {s.desc}
                <ul>
                  {s.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <div className="dashboard-item-actions">
                  <button onClick={() => updateService(s.id)}>Edit</button>
                  <button onClick={() => deleteService(s.id)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------- Blogs Section ---------------- */}
      {activeTab === 4 && (
        <div className="dashboard-section">
          <h2>Blogs</h2>
          <div className="dashboard-form">
            <input
              placeholder="Title"
              value={blogTitle}
              onChange={(e) => setBlogTitle(e.target.value)}
            />
            <input
              placeholder="Summary"
              value={blogSummary}
              onChange={(e) => setBlogSummary(e.target.value)}
            />
            <textarea
              placeholder="Content"
              value={blogContent}
              onChange={(e) => setBlogContent(e.target.value)}
            />
            <input
              placeholder="Image URL"
              value={blogImage}
              onChange={(e) => setBlogImage(e.target.value)}
            />
            <input
              placeholder="Category"
              value={blogCategory}
              onChange={(e) => setBlogCategory(e.target.value)}
            />
            <input
              placeholder="Date (YYYY-MM-DD)"
              value={blogDate}
              onChange={(e) => setBlogDate(e.target.value)}
            />
            <button onClick={addBlog}>Add Blog</button>
          </div>

          <ul>
            {blogs.map((b) => (
              <li key={b.id}>
                <strong>{b.title}</strong> – {b.summary} ({b.category}) [{b.date}]
                {b.image && (
                  <div>
                    <img
                      src={b.image}
                      alt={b.title}
                      width="100"
                      style={{ marginTop: "5px", borderRadius: "6px" }}
                    />
                  </div>
                )}
                <div className="dashboard-item-actions">
                  <button onClick={() => updateBlog(b.id)}>Edit</button>
                  <button onClick={() => deleteBlog(b.id)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------- Logout Button at the Bottom ---------------- */}
      <div style={{ marginTop: "40px", textAlign: "center" }}>
        <button
          className="logout-btn"
          onClick={async () => {
            try {
              const res = await fetch("http://localhost:5000/api/auth/logout", {
                method: "POST",
                credentials: "include",
              });
              const data = await res.json();
              if (data.success) window.location.href = "/admin";
            } catch (err) {
              console.error("Logout failed:", err);
              alert("Logout failed. Try again.");
            }
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
